import { useMemo, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ChevronLeft, ChevronRight, CalendarDays, Phone, Stethoscope } from "lucide-react";

const DOW = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const tones = ["bg-teal-500", "bg-amber-500", "bg-sky-500", "bg-fuchsia-500"];

function ymd(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

export default function CalendarView({ batches, patients, orthodontists = [] }) {
  const [doctor, setDoctor] = useState("all");
  const dated = batches.filter((b) => b.date);
  const firstDated = dated.length
    ? new Date(dated.map((b) => b.date).sort()[0] + "T00:00:00")
    : new Date();
  const [view, setView] = useState({ year: firstDated.getFullYear(), month: firstDated.getMonth() });
  const [selected, setSelected] = useState(null);

  const batchByDate = useMemo(() => {
    const map = {};
    batches.forEach((b, i) => {
      if (!b.date) return;
      (map[b.date] = map[b.date] || []).push({ ...b, tone: tones[i % tones.length] });
    });
    return map;
  }, [batches]);

  const cells = useMemo(() => {
    const first = new Date(view.year, view.month, 1);
    const start = first.getDay();
    const days = new Date(view.year, view.month + 1, 0).getDate();
    const arr = [];
    for (let i = 0; i < start; i++) arr.push(null);
    for (let d = 1; d <= days; d++) arr.push(new Date(view.year, view.month, d));
    return arr;
  }, [view]);

  const monthLabel = new Date(view.year, view.month, 1).toLocaleDateString(undefined, {
    month: "long",
    year: "numeric",
  });

  const move = (delta) => {
    setSelected(null);
    setView((v) => {
      const m = v.month + delta;
      return { year: v.year + Math.floor(m / 12), month: ((m % 12) + 12) % 12 };
    });
  };

  const selectedBatches = selected ? batchByDate[selected] || [] : [];
  const patientsInBatch = (label) =>
    patients.filter(
      (p) =>
        (p.batch === label || p.extra_batch === label) &&
        !p.carryover_next_month &&
        (doctor === "all" || (doctor === "none" ? !p.orthodontist : p.orthodontist === doctor))
    );

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
      <Card className="p-5 border-slate-200 lg:col-span-2" data-testid="calendar-card">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
          <h3 className="font-heading font-semibold text-lg text-slate-900">{monthLabel}</h3>
          <div className="flex items-center gap-2">
            <Select value={doctor} onValueChange={setDoctor}>
              <SelectTrigger data-testid="calendar-doctor-filter" className="h-8 w-[180px] rounded-full text-xs">
                <Stethoscope className="h-3.5 w-3.5 text-teal-600 mr-1" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="all">All Orthodontists</SelectItem>
                {orthodontists.map((o) => (
                  <SelectItem key={o.id} value={o.name}>{o.name}</SelectItem>
                ))}
                <SelectItem value="none">Not assigned</SelectItem>
              </SelectContent>
            </Select>
            <Button data-testid="calendar-prev" size="icon" variant="outline" className="h-8 w-8 rounded-full" onClick={() => move(-1)}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button data-testid="calendar-next" size="icon" variant="outline" className="h-8 w-8 rounded-full" onClick={() => move(1)}>
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
        <div className="grid grid-cols-7 gap-1.5">
          {DOW.map((d) => (
            <div key={d} className="text-center text-[11px] font-medium uppercase tracking-wider text-slate-400 py-1">
              {d}
            </div>
          ))}
          {cells.map((date, i) => {
            if (!date) return <div key={`e${i}`} />;
            const key = ymd(date);
            const dayBatches = batchByDate[key] || [];
            const isClinic = dayBatches.length > 0;
            const isSel = selected === key;
            return (
              <button
                key={key}
                data-testid={`calendar-day-${key}`}
                onClick={() => isClinic && setSelected(isSel ? null : key)}
                disabled={!isClinic}
                className={`min-h-[68px] rounded-lg border p-1.5 text-left transition-colors ${
                  isSel
                    ? "border-accent ring-2 ring-accent/30 bg-accent/5"
                    : isClinic
                    ? "border-slate-200 bg-white hover:border-accent cursor-pointer"
                    : "border-transparent bg-slate-50/50 cursor-default"
                }`}
              >
                <span className={`text-xs font-medium ${isClinic ? "text-slate-900" : "text-slate-400"}`}>
                  {date.getDate()}
                </span>
                <div className="mt-1 space-y-0.5">
                  {dayBatches.map((b) => (
                    <span
                      key={b.label}
                      className="flex items-center gap-1 text-[10px] font-medium text-slate-600"
                    >
                      <span className={`h-1.5 w-1.5 rounded-full ${b.tone}`} />
                      {b.label} · {patientsInBatch(b.label).length}
                    </span>
                  ))}
                </div>
              </button>
            );
          })}
        </div>
      </Card>

      <Card className="p-5 border-slate-200" data-testid="calendar-detail">
        {!selected ? (
          <div className="text-center text-sm text-slate-400 py-12">
            <CalendarDays className="h-8 w-8 mx-auto mb-3 text-slate-300" />
            Select a clinic day to see who's booked.
          </div>
        ) : (
          <>
            <p className="text-xs uppercase tracking-wider text-slate-500 mb-1">
              Booked on{doctor !== "all" && ` · ${doctor === "none" ? "No orthodontist" : doctor}`}
            </p>
            <h3 className="font-heading font-semibold text-slate-900 mb-4">
              {new Date(selected + "T00:00:00").toLocaleDateString(undefined, {
                weekday: "long",
                day: "numeric",
                month: "long",
              })}
            </h3>
            <div className="space-y-4">
              {selectedBatches.map((b) => {
                const list = patientsInBatch(b.label);
                return (
                  <div key={b.label}>
                    <div className="flex items-center gap-2 mb-2">
                      <span className={`h-2.5 w-2.5 rounded-full ${b.tone}`} />
                      <span className="font-semibold text-sm text-slate-800">Batch {b.label}</span>
                      <Badge className="bg-slate-100 text-slate-600 hover:bg-slate-100 rounded-full text-[11px]">
                        {list.length}/{b.capacity}
                      </Badge>
                    </div>
                    {list.length === 0 ? (
                      <p className="text-xs text-slate-400 pl-4">No patients scheduled.</p>
                    ) : (
                      <ul className="space-y-1.5">
                        {list.map((p) => (
                          <li
                            key={p.id}
                            data-testid={`calendar-patient-${p.register_number}`}
                            className="flex items-center justify-between rounded-lg border border-slate-200 px-2.5 py-1.5"
                          >
                            <div className="min-w-0">
                              <p className="text-sm font-medium text-slate-800 truncate">{p.name}</p>
                              <p className="text-[11px] text-slate-500 font-mono">{p.register_number}</p>
                            </div>
                            <a
                              href={`tel:${p.phone}`}
                              className="flex items-center gap-1 text-xs text-accent shrink-0"
                            >
                              <Phone className="h-3 w-3" /> {p.phone}
                            </a>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                );
              })}
            </div>
          </>
        )}
      </Card>
    </div>
  );
}
